import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { getClientFingerprint } from "@/lib/fingerprint";
import { Snowflake, Star, Leaf, Flower2, Sun, Heart, Moon, PartyPopper, X, Sparkles, Volume2, VolumeX } from "lucide-react";
import { AdventAmbience } from "@/components/AdventAmbience";
import AdventDoor from "@/components/AdventDoor";
import { adventBackground, adventTheme } from "@/lib/adventThemes";
import { startAmbient, stopAmbient, playDoorOpen, setAmbientMuted } from "@/lib/adventMusic";
import { playerErrorMessage } from "@/lib/playerErrors";

/* "Coffret de fêtes" advent calendar: themed ambience (lib/adventThemes) under a
   gilded jewel-box armature, lacquered 3D-flip doors, hero last door. */

// Door grids that close perfectly with a featured last door (2×1 or 2×2).
const ADVENT_LAYOUTS = {
  24: { cols: "grid-cols-5", hero: "col-span-2" },
  12: { cols: "grid-cols-3 sm:grid-cols-5", hero: "col-span-2 row-span-2 col-start-2 row-start-4 sm:col-start-4 sm:row-start-2" },
  7: { cols: "grid-cols-4", hero: "col-span-2" },
};

const COFFRET_SHADOW =
  "0 25px 70px -15px rgba(0,0,0,0.7), 0 0 0 1px rgba(226,184,114,0.22), inset 0 1px 0 0 rgba(255,255,255,0.15), inset 0 0 30px rgba(0,0,0,0.45)";

const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

const ADVENT_CSS = `
  .advent-door { perspective: 750px; }
  .advent-door-inner { transform-style: preserve-3d; transition: transform .75s cubic-bezier(.19,1,.22,1); }
  .advent-door.is-open .advent-door-inner { transform: rotateY(-180deg); }
  .advent-face { backface-visibility: hidden; -webkit-backface-visibility: hidden; }
  .advent-face.back { transform: rotateY(180deg); }
  .advent-front { transition: transform .3s ease, border-color .3s ease, box-shadow .3s ease; }
  .advent-door:not(:disabled):hover .advent-available { transform: translateY(-3px); border-color: rgba(248,228,182,.9) !important; box-shadow: 0 10px 25px -5px rgba(226,184,114,.4), inset 0 1px 0 rgba(255,255,255,.3) !important; }
  .advent-available { animation: advent-gold-breathe 2.8s ease-in-out infinite; }
  .advent-gift { animation: advent-gift-glow 2.2s ease-in-out infinite; }
  .advent-enter { animation: advent-rise-in .6s cubic-bezier(.2,.8,.2,1) both; }
  @keyframes advent-gold-breathe { 0%,100% { box-shadow: inset 0 1px 0 rgba(255,255,255,.28), inset 0 -2px 4px rgba(0,0,0,.4), 0 0 0 0 rgba(226,184,114,0); } 50% { box-shadow: inset 0 1px 0 rgba(255,255,255,.28), inset 0 -2px 4px rgba(0,0,0,.4), 0 0 16px 2px rgba(226,184,114,.38), inset 0 0 10px rgba(226,184,114,.18); } }
  @keyframes advent-gift-glow { 0%,100% { filter: drop-shadow(0 0 2px rgba(248,228,182,.3)); } 50% { filter: drop-shadow(0 0 8px rgba(248,228,182,.8)); } }
  @keyframes advent-rise-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  @media (prefers-reduced-motion: reduce) {
    .advent-door-inner { transition-duration: .01s; }
    .advent-available, .advent-gift, .advent-enter { animation: none; }
  }
`;

const LOSE_ICONS = {
  snow: Snowflake,
  stars: Star,
  autumn: Leaf,
  spring: Flower2,
  summer: Sun,
  valentine: Heart,
  ramadan: Moon,
  birthday: PartyPopper,
  none: X,
};

function useCountdownToNextUtcMidnight(active) {
  const [txt, setTxt] = useState("");
  useEffect(() => {
    if (!active) return;
    const tick = () => {
      const now = new Date();
      const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
      let s = Math.max(0, Math.floor((next - now) / 1000));
      const h = String(Math.floor(s / 3600)).padStart(2, "0");
      const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
      const sec = String(s % 60).padStart(2, "0");
      setTxt(`${h}:${m}:${sec}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [active]);
  return txt;
}

export default function AdventCalendar({
  game,
  participant,
  ctasCompleted,
  receipt,
  onAlreadyParticipated,
  onResult,
  muted = false,
  onToggleMute,
}) {
  const { t } = useTranslation();
  const days = Math.max(1, parseInt(game.advent_days || 24, 10));
  const mode = game.advent_mode || "daily";
  const catchup = !!game.advent_catchup;
  const themeKey = adventTheme(game);
  const LoseIcon = LOSE_ICONS[themeKey] || Snowflake;
  const soundMode = game.advent_sound || "music_sfx";
  const sfxOn = soundMode !== "off";
  const musicOn = soundMode === "music_sfx";

  // Themed looping motif while the calendar is on screen (player can mute).
  useEffect(() => {
    setAmbientMuted(muted);
    if (musicOn && !muted) startAmbient(themeKey);
    return () => stopAmbient();
  }, [musicOn, muted, themeKey]);

  const [opened, setOpened] = useState({}); // day -> {is_win, result_label}
  const [todayDay, setTodayDay] = useState(null);
  const [openingDay, setOpeningDay] = useState(null);
  const [revealDay, setRevealDay] = useState(null); // door showing its content
  const inFlight = useRef(false);

  async function fetchState() {
    const { data } = await axios.post(`${API}/public/games/${game.slug}/advent-state`, {
      email: participant?.email || null,
      phone: participant?.phone || null,
      fingerprint: getClientFingerprint(),
    });
    const map = {};
    (data.opened || []).forEach((o) => (map[o.day] = o));
    setOpened(map);
    setTodayDay(data.today_day);
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (let attempt = 0; attempt < 2 && !cancelled; attempt += 1) {
        try {
          await fetchState();
          return;
        } catch {
          await new Promise((r) => setTimeout(r, 1200));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.slug]);

  const freeDone = mode === "free" && Object.keys(opened).length > 0;

  function doorState(day) {
    if (opened[day]) return "opened";
    if (mode === "free") return freeDone ? "disabled" : "available";
    if (todayDay == null) return "locked";
    if (day > todayDay) return "locked";
    if (day < todayDay && !catchup) return "missed";
    return "available";
  }

  async function openDoor(day) {
    if (inFlight.current) return;
    if (doorState(day) !== "available") return;
    inFlight.current = true;
    setOpeningDay(day);
    if (sfxOn && !muted) playDoorOpen(themeKey);
    try {
      const { data } = await axios.post(`${API}/public/games/${game.slug}/advent-open`, {
        participant,
        ctas_completed: ctasCompleted || [],
        fingerprint: getClientFingerprint(),
        receipt_url: receipt?.url || null,
        receipt_ocr: receipt?.ocr || null,
        day,
      });
      setOpened((m) => ({ ...m, [day]: { day, is_win: data.is_win, result_label: data.result_label } }));
      setTimeout(() => setRevealDay(day), 350);
      setTimeout(() => {
        onResult && onResult(data);
        setOpeningDay(null);
        inFlight.current = false;
      }, 1600);
    } catch (e) {
      setOpeningDay(null);
      inFlight.current = false;
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail.code : null;
      const msg = detail ? playerErrorMessage(e) : null;
      if (code === "door_already_opened") {
        setOpened((m) => ({ ...m, [day]: { day, is_win: false, result_label: null } }));
        toast.info(msg || t("play.advent_already_opened", "Vous avez déjà ouvert cette case."));
        fetchState().catch(() => {});
      } else if (code === "already_participated" || e?.response?.status === 409) {
        onAlreadyParticipated && onAlreadyParticipated(msg || "");
      } else {
        toast.error(msg || t("play.advent_error", "Impossible d'ouvrir cette case pour le moment."));
      }
    }
  }

  const hasLockedDoors = mode === "daily" && todayDay != null && todayDay < days;
  const countdown = useCountdownToNextUtcMidnight(hasLockedDoors);
  const todayOpened = mode === "daily" && todayDay != null && !!opened[Math.min(todayDay, days)];
  const openedCount = Object.keys(opened).length;
  const allOpened = mode === "daily" && openedCount >= days;
  const winsCount = Object.values(opened).filter((o) => o?.is_win).length;

  const layout = ADVENT_LAYOUTS[days] || { cols: days > 12 ? "grid-cols-4 sm:grid-cols-6" : "grid-cols-3 sm:grid-cols-4", hero: null };
  const todayIdx = mode === "daily" && todayDay != null ? Math.min(todayDay, days) : null;

  return (
    <div className="w-full max-w-2xl mx-auto" data-testid="advent-calendar">
      <style>{ADVENT_CSS}</style>

      <div
        className="relative rounded-[28px] sm:rounded-[32px] p-4 sm:p-7 overflow-hidden"
        style={{ background: adventBackground(game), boxShadow: COFFRET_SHADOW }}
      >
        <AdventAmbience game={game} className="rounded-[28px] sm:rounded-[32px]" />
        {/* Coffret framing: inner gilded hairline, top sheen, archival grain */}
        <div className="pointer-events-none absolute inset-2.5 sm:inset-3 rounded-[22px] sm:rounded-[26px] border border-amber-200/15" aria-hidden="true" />
        <div className="pointer-events-none absolute left-1/2 top-0 h-24 w-64 -translate-x-1/2 bg-gradient-to-b from-amber-300/10 to-transparent blur-2xl" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.035] mix-blend-overlay" style={{ backgroundImage: GRAIN }} aria-hidden="true" />

        <div className="relative flex flex-wrap items-center justify-between gap-2 sm:gap-3 mb-5 sm:mb-6 pb-3 border-b border-white/10">
          <div
            className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-400/[0.08] backdrop-blur-md px-3.5 py-1.5 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-200 shadow-[0_2px_12px_rgba(0,0,0,0.3)]"
            style={{ fontFamily: "'Cinzel', Georgia, serif" }}
            data-testid="advent-mode-badge"
          >
            <Sparkles className="h-3.5 w-3.5 stroke-[1.75] text-amber-300" />
            {mode === "daily"
              ? t("play.advent_daily_badge", "Une case par jour")
              : t("play.advent_free_badge", "Une seule case au choix")}
          </div>
          <div className="flex items-center gap-2">
            {mode === "daily" && todayDay != null && todayDay >= 1 && todayDay <= days && (
              <div
                className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.05] px-2.5 sm:px-3 py-1 text-[10px] sm:text-[12px] font-medium uppercase tracking-[0.14em] text-white/80 whitespace-nowrap"
                data-testid="advent-today-label"
              >
                {t("play.advent_day", "Jour")} <span className="font-semibold text-amber-300">{Math.min(todayDay, days)}</span> / {days}
              </div>
            )}
            {sfxOn && onToggleMute && (
              <button
                type="button"
                onClick={onToggleMute}
                className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-white/15 bg-white/[0.06] text-amber-200/80 shadow-sm transition-[background-color,border-color,color] duration-300 hover:border-amber-300/40 hover:bg-white/[0.14] hover:text-amber-100"
                aria-label={muted ? t("play.advent_sound_on", "Activer le son") : t("play.advent_sound_off", "Couper le son")}
                title={muted ? t("play.advent_sound_on", "Activer le son") : t("play.advent_sound_off", "Couper le son")}
                data-testid="advent-sound-toggle"
              >
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
            )}
          </div>
        </div>

        <div className={`relative grid ${layout.cols} gap-2 sm:gap-3.5`} data-testid="advent-grid">
          {Array.from({ length: days }, (_, i) => {
            const day = i + 1;
            return (
              <AdventDoor
                key={day}
                day={day}
                index={i}
                state={doorState(day)}
                opened={opened[day]}
                isOpening={openingDay === day}
                reveal={revealDay === day}
                isToday={todayIdx === day}
                hero={!!layout.hero && day === days}
                heroClass={layout.hero || ""}
                LoseIcon={LoseIcon}
                onOpen={openDoor}
                image={game.advent_door_images?.[String(day)] || null}
                t={t}
              />
            );
          })}
        </div>

        <div className="relative mt-6 text-center">
          {allOpened ? (
            <div
              className="mx-auto max-w-md rounded-2xl border border-amber-300/45 bg-gradient-to-b from-amber-400/15 to-amber-950/40 px-5 py-4 backdrop-blur-lg shadow-[0_15px_35px_-10px_rgba(226,184,114,0.25)]"
              data-testid="advent-all-opened"
            >
              <div
                className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold uppercase tracking-[0.18em] text-amber-200"
                style={{ fontFamily: "'Cinzel', Georgia, serif" }}
              >
                <Sparkles className="h-3.5 w-3.5" />
                {t("play.advent_all_opened_kicker", "Calendrier terminé")}
              </div>
              <p className="mt-1.5 text-[13px] font-medium leading-relaxed text-white/90">
                {t("play.advent_all_opened", "Toutes les cases sont ouvertes ! Merci d'avoir joué avec nous.")}
              </p>
              <p className="mt-0.5 text-[12px] text-white/70" data-testid="advent-all-opened-wins">
                {winsCount > 0
                  ? t("play.advent_all_opened_wins", { count: winsCount })
                  : t("play.advent_all_opened_nowin", "Pas de lot cette fois-ci — à très bientôt pour une nouvelle opération !")}
              </p>
            </div>
          ) : (
            <div
              className="mx-auto inline-flex max-w-full flex-col items-center justify-center gap-1.5 rounded-2xl border border-white/10 bg-black/25 px-4 py-3 backdrop-blur-md sm:flex-row sm:gap-3"
              data-testid="advent-hint"
            >
              <span className="text-[12px] sm:text-[13px] font-medium tracking-wide text-white/75">
                {mode === "free"
                  ? freeDone
                    ? t("play.advent_free_done", "Votre case est ouverte — bonne chance pour la prochaine opération !")
                    : t("play.advent_pick_one", "Choisissez votre case porte-bonheur — une seule !")
                  : todayOpened && hasLockedDoors
                    ? t("play.advent_come_back", "Case du jour ouverte ! Prochaine case dans")
                    : hasLockedDoors
                      ? t("play.advent_open_today", "Ouvrez la case du jour !")
                      : t("play.advent_open_any", "Ouvrez votre case !")}
              </span>
              {mode === "daily" && hasLockedDoors && (
                <span className="inline-flex items-center gap-1.5 text-[11px] sm:text-[12px] text-white/50">
                  {!todayOpened && t("play.advent_next_in", "Prochaine case dans")}
                  <span className="rounded-md border border-amber-300/30 bg-amber-400/10 px-2.5 py-0.5 font-mono text-[13px] sm:text-[14px] font-semibold tracking-wider text-amber-300 shadow-[0_0_12px_rgba(226,184,114,0.2)]">
                    {countdown}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
